import React, {
    createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import GA4React from 'ga-4-react';

const GAContext = createContext();

export const GoogleAnalyticsProvider = ({ children }) => {
    const [ga, setGa] = useState(null);
    const location = useLocation();

    useEffect(() => {
        if (process.env.REACT_APP_GA4) {
            const ga4React = new GA4React(
                process.env.REACT_APP_GA4,
                undefined,
                undefined,
                // timeout for initialization (waiting on script to load)
                // bumped up to 30,000 b/c was seeing a lot of failed initialization / timeout
                // errors, working theory is tied to poor connections, so bumped up from
                // default of 5 seconds to hopefully better align with longer page load times
                30000,
            );

            ga4React.initialize().then(() => {
                setGa(ga4React);
            });
            // Let promise rejection bubble for monitoring in Sentry
        }
    }, []);

    useEffect(() => {
        if (ga) {
            ga.pageview(location.pathname, location.search, location.pathname);
        }
    }, [location, ga]);

    const ctx = useMemo(() => ({ ga }), [ga]);

    return (
        <GAContext.Provider value={ctx}>
            {children}
        </GAContext.Provider>
    );
};

GoogleAnalyticsProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useGA4 = () => {
    const context = useContext(GAContext);
    if (!context) {
        throw new Error('useGA4 must be used within GoogleAnalyticsProvider');
    }
    return context.ga;
};

/*

Note some configuration in GA is needed to support receiving and reporting on these events:

- Create a new event definition in GA4 for each event type (e.g. CTA_CLICKED, RESOURCES_TAB_CLICKED): https://support.google.com/analytics/answer/12229021?hl=en
- Create custom dimension for each event parameter (e.g. cta_name, category) on which we want to report: https://support.google.com/analytics/answer/14239696

Once those are set, GA will be able to import these events into reports / refer to them in custom report / explore views.

*/

export const EVENTS = {
    CTA_CLICKED: 'cta_clicked', // params: cta_name
    RESOURCES_TAB_CLICKED: 'resources_tab_clicked', // params: category
    CHANGE_LANGUAGE_CLICKED: 'update_language_clicked', // params: language
};

// https://developers.google.com/analytics/devguides/collection/ga4/events?client_type=gtag
export const useGACustomEvent = () => {
    const ga = useGA4();
    return (event, parameters) => {
        if (Object.values(EVENTS).includes(event)) {
            // Drill down to the gtag instance since ga4React's event method is opinionated re:
            // parameters passed: https://github.com/unrealmanu/ga-4-react/blob/main/src/lib/ga4manager.tsx#L210-L221
            if (ga) {
                ga.gtag('event', event, parameters);
            }
        }
    };
};
